/**
 * Reseller Notices tab (TMMPDM-0038, tmm-api 0100): every standing email sent about the reseller,
 * newest first — Sent / created · Kind · Period · Status · Recipients. Kinds and statuses are words
 * plus a chip (STATE-001/002); a `SKIPPED` or `PENDING` status explains itself in its tooltip.
 * Recipients are a count only: the addresses are never on the wire, never typed, never rendered.
 * Expanding a row lists the facts its email stated — only the keys present — with money through
 * `Amount` in the notice's currency, `charges[]` as a mini table, `toResellerId` as the reseller's
 * name link and `tenantsMoved` as a count. TMMPDM-0035: the `kind` filter lives in the URL (`kind`,
 * beside `?tab=`; default all kinds).
 */
import { Fragment, useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { EmptyState, ErrorState } from '@/components/page';
import { Amount } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import {
  RESELLER_NOTICE_KINDS,
  type ResellerNoticeCharge,
  type ResellerNoticeFacts,
  type ResellerNoticeKind,
  type ResellerNoticeStatus,
  type ResellerNoticeView,
} from '@/api/tmm/types';
import { formatDate, formatInstant, formatMonthYear } from '@/format';
import { ResellerChargeChip } from './ResellerChargeChip';
import { CHARGE_KIND_FALLBACK } from './settlementFormat';
import { resellerLabel, useResellerNames } from './useResellerNames';
import { StatusChip } from '@/status';

interface ResellerNoticesPanelProps {
  resellerId: number;
}

const NOTICE_KIND_FALLBACK: Record<ResellerNoticeKind, string> = {
  LAUNCH_REMINDER: 'Launch reminder',
  PROBATION_STARTED: 'Probation started',
  PROBATION_MONTHLY: 'Probation update',
  CHARGE_FAILED: 'Charge failed',
  CHARGE_ACTION_REQUIRED: 'Charge needs action',
  DEACTIVATED: 'Deactivated',
  RECOVERED: 'Recovered',
};

const NOTICE_STATUS_FALLBACK: Record<ResellerNoticeStatus, string> = {
  PENDING: 'Pending',
  SENDING: 'Sending',
  SENT: 'Sent',
  SKIPPED: 'Skipped',
  FAILED: 'Failed',
};

/** The statuses whose chip explains itself in a tooltip. */
const STATUS_HINT_FALLBACK: Partial<Record<ResellerNoticeStatus, string>> = {
  PENDING: 'Not sent yet. The notice engine sends it within the hour.',
  SKIPPED: 'Not sent: mail not configured, no recipient, or no longer needed',
};

const COLUMNS = 6;
const ALL = '';

type NoticesView = { kind: ResellerNoticeKind | typeof ALL };
const VIEW: UrlSchema<NoticesView> = {
  kind: { default: ALL, parse: oneOf(RESELLER_NOTICE_KINDS) },
};

/** A launch reminder's period is its due day; every other kind's is a month's 1st. */
function noticePeriodLabel(notice: ResellerNoticeView, locale: string): string {
  return notice.kind === 'LAUNCH_REMINDER'
    ? formatDate(notice.periodOn, locale)
    : formatMonthYear(notice.periodOn, locale);
}

function hasFacts(facts: ResellerNoticeFacts): boolean {
  return Object.values(facts).some((value) => value !== undefined);
}

export function ResellerNoticesPanel({ resellerId }: ResellerNoticesPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [view, setView] = useUrlState(VIEW);
  const kind = view.kind;
  const setKind = (value: ResellerNoticeKind | typeof ALL) => setView({ kind: value });
  const [reloadToken, setReloadToken] = useState(0);
  const [notices, setNotices] = useState<ResellerNoticeView[] | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const { names } = useResellerNames();

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listResellerNotices(resellerId)
      .then((list) => {
        if (cancelled) return;
        // A non-list body (an HTML fallback, a wrong payload) is a load failure, not a throw
        // that takes the Standing tab with it.
        if (!Array.isArray(list?.notices)) {
          setNotices(null);
          setFailure(list);
          return;
        }
        setNotices(list.notices);
        setFailure(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setNotices(null);
        setFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, reloadToken]);

  const reload = () => setReloadToken((n) => n + 1);
  const kindLabel = (value: ResellerNoticeKind) =>
    t(`resellers.notices.kind.${value}`, NOTICE_KIND_FALLBACK[value]);

  if (failure !== null) {
    return (
      <ErrorState
        error={failure}
        message={t('resellers.notices.loadError', 'Could not load notices.')}
        onRetry={reload}
      />
    );
  }

  const rows = notices === null ? null : notices.filter((n) => kind === ALL || n.kind === kind);
  const filtered = kind !== ALL;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="notices-kind-label">
            {t('resellers.notices.filter.kind', 'Kind')}
          </InputLabel>
          <Select
            labelId="notices-kind-label"
            value={kind}
            label={t('resellers.notices.filter.kind', 'Kind')}
            onChange={(event) => setKind(event.target.value)}
          >
            <MenuItem value={ALL}>{t('resellers.notices.filter.allKinds', 'All kinds')}</MenuItem>
            {RESELLER_NOTICE_KINDS.map((value) => (
              <MenuItem key={value} value={value}>
                {kindLabel(value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('resellers.notices.tableAria', 'Notices')}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t('resellers.notices.col.when', 'Sent / created')}</TableCell>
              <TableCell>{t('resellers.notices.col.kind', 'Kind')}</TableCell>
              <TableCell>{t('resellers.notices.col.period', 'Period')}</TableCell>
              <TableCell>{t('resellers.notices.col.status', 'Status')}</TableCell>
              <TableCell>{t('resellers.notices.col.recipients', 'Recipients')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows === null ? (
              <TableRow>
                <TableCell colSpan={COLUMNS} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.loading', 'Loading')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS} align="center" sx={{ py: 2 }}>
                  <EmptyState
                    title={
                      filtered
                        ? t('resellers.notices.emptyFiltered', 'No notices match this filter.')
                        : t('resellers.notices.empty', 'No notices yet.')
                    }
                    {...(filtered ? { filtered: { onClear: () => setKind(ALL) } } : {})}
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((notice) => {
                const open = expanded === notice.id;
                const kindWords = kindLabel(notice.kind);
                const period = noticePeriodLabel(notice, locale);
                const expandable = hasFacts(notice.facts);
                return (
                  <Fragment key={notice.id}>
                    <TableRow hover>
                      <TableCell>
                        {expandable ? (
                          <IconButton
                            size="small"
                            aria-expanded={open}
                            aria-label={
                              open
                                ? t('resellers.notices.collapse', 'Collapse {{kind}}, {{period}}', {
                                    kind: kindWords,
                                    period,
                                  })
                                : t('resellers.notices.expand', 'Expand {{kind}}, {{period}}', {
                                    kind: kindWords,
                                    period,
                                  })
                            }
                            onClick={() => setExpanded(open ? null : notice.id)}
                          >
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {formatInstant(notice.sentAt ?? notice.createdAt, locale)}
                        {notice.sentAt === undefined ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {t('resellers.notices.created', 'Created, not sent')}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{kindWords}</TableCell>
                      <TableCell>{period}</TableCell>
                      <TableCell>
                        <NoticeStatusChip status={notice.status} />
                      </TableCell>
                      <TableCell>
                        {notice.recipientCount !== undefined
                          ? t('resellers.notices.recipients', '{{n}} recipients', {
                              n: notice.recipientCount,
                            })
                          : '—'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={COLUMNS}
                        sx={{ py: 0, borderBottom: open ? undefined : 'none' }}
                      >
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1 }}>
                            <NoticeFacts
                              facts={notice.facts}
                              label={t(
                                'resellers.notices.factsAria',
                                'Facts: {{kind}}, {{period}}',
                                {
                                  kind: kindWords,
                                  period,
                                },
                              )}
                              locale={locale}
                              names={names}
                            />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function NoticeStatusChip({ status }: { status: string }) {
  const { t } = useCopy();
  if (!Object.hasOwn(NOTICE_STATUS_FALLBACK, status)) {
    return <StatusChip label={status} />;
  }
  const known = status as ResellerNoticeStatus;
  const chip = (
    <StatusChip
      status={known}
      label={t(`resellers.notices.status.${known}`, NOTICE_STATUS_FALLBACK[known])}
    />
  );
  const hint = STATUS_HINT_FALLBACK[known];
  if (hint === undefined) return chip;
  // Focusable so the explanation reaches keyboard users too (A11Y-001).
  return (
    <Tooltip title={t(`resellers.notices.statusHint.${known}`, hint)} describeChild>
      <Box component="span" tabIndex={0} sx={{ display: 'inline-flex' }}>
        {chip}
      </Box>
    </Tooltip>
  );
}

function NoticeFacts({
  facts,
  label,
  locale,
  names,
}: {
  facts: ResellerNoticeFacts;
  label: string;
  locale: string;
  names: Map<number, string>;
}) {
  const { t } = useCopy();
  const { currency } = facts;
  const lines: { key: string; label: string; value: ReactNode }[] = [];
  const money = (key: keyof ResellerNoticeFacts, words: string, minor: number | undefined) => {
    // Money is stated in the notice's currency; without one it can't be shown honestly.
    if (minor === undefined || currency === undefined) return;
    lines.push({
      key,
      label: words,
      value: <Amount minor={minor} currency={currency} locale={locale} />,
    });
  };

  if (facts.month !== undefined) {
    lines.push({
      key: 'month',
      label: t('resellers.notices.fact.month', 'Month'),
      value: formatMonthYear(facts.month, locale),
    });
  }
  if (currency !== undefined) {
    lines.push({
      key: 'currency',
      label: t('resellers.notices.fact.currency', 'Currency'),
      value: currency,
    });
  }
  money('shortfallMinor', t('resellers.notices.fact.shortfall', 'Shortfall'), facts.shortfallMinor);
  if (facts.probationMonths !== undefined) {
    lines.push({
      key: 'probationMonths',
      label: t('resellers.notices.fact.probationMonths', 'Probation months'),
      value: <Amount count={facts.probationMonths} locale={locale} />,
    });
  }
  money(
    'targetMinor',
    t('resellers.notices.fact.target', 'Minimum for the probation window'),
    facts.targetMinor,
  );
  money('earnedMinor', t('resellers.notices.fact.earned', 'Earned so far'), facts.earnedMinor);
  money(
    'stillNeededMinor',
    t('resellers.notices.fact.stillNeeded', 'Still needed'),
    facts.stillNeededMinor,
  );
  if (facts.deactivationOn !== undefined) {
    lines.push({
      key: 'deactivationOn',
      label: t('resellers.notices.fact.deactivationOn', 'Deactivation date'),
      value: formatDate(facts.deactivationOn, locale),
    });
  }
  if (facts.launchOn !== undefined) {
    lines.push({
      key: 'launchOn',
      label: t('resellers.notices.fact.launchOn', 'Official launch'),
      value: formatDate(facts.launchOn, locale),
    });
  }
  if (facts.toResellerId !== undefined) {
    lines.push({
      key: 'toResellerId',
      label: t('resellers.notices.fact.toReseller', 'Tenants moved to'),
      value: (
        <Link component={RouterLink} to={`/partners/${facts.toResellerId}`} underline="always">
          {resellerLabel(names, facts.toResellerId)}
        </Link>
      ),
    });
  }
  if (facts.tenantsMoved !== undefined) {
    lines.push({
      key: 'tenantsMoved',
      label: t('resellers.notices.fact.tenantsMoved', 'Tenants moved'),
      value: <Amount count={facts.tenantsMoved} locale={locale} />,
    });
  }

  return (
    <Stack spacing={1}>
      {lines.length > 0 ? (
        <Table size="small" aria-label={label} sx={{ maxWidth: 560 }}>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.key}>
                <TableCell component="th" scope="row">
                  {line.label}
                </TableCell>
                <TableCell>{line.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {facts.charges !== undefined ? (
        <NoticeCharges charges={facts.charges} locale={locale} />
      ) : null}
    </Stack>
  );
}

function NoticeCharges({ charges, locale }: { charges: ResellerNoticeCharge[]; locale: string }) {
  const { t } = useCopy();
  return (
    <Table size="small" aria-label={t('resellers.notices.chargesAria', 'Charges in this notice')}>
      <TableHead>
        <TableRow>
          <TableCell>{t('resellers.notices.charge.kind', 'Charge')}</TableCell>
          <TableCell align="right">{t('resellers.notices.charge.amount', 'Amount')}</TableCell>
          <TableCell>{t('resellers.notices.charge.status', 'Status')}</TableCell>
          <TableCell>{t('resellers.notices.charge.holdOn', 'Holds payouts from')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {charges.map((charge) => (
          <TableRow key={charge.id}>
            <TableCell>
              {t(`resellerBilling.kind.${charge.kind}`, CHARGE_KIND_FALLBACK[charge.kind])}
            </TableCell>
            <TableCell align="right">
              <Amount minor={charge.amountMinor} currency={charge.currency} locale={locale} />
            </TableCell>
            <TableCell>
              <ResellerChargeChip status={charge.status} />
            </TableCell>
            <TableCell>{formatDate(charge.holdOn, locale)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
